(function () {
  if (customElements.get('collection-image-tabs')) return;

  class CollectionImageTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.configElement = this.querySelector('[data-collection-tabs-config]');
      this.tabs = Array.from(this.querySelectorAll('[data-collection-tab]'));
      this.panel = this.querySelector('[data-collection-products-panel]');
      this.grid = this.querySelector('[data-collection-products-grid]');
      this.status = this.querySelector('[data-collection-products-status]');
      this.viewAll = this.querySelector('[data-collection-view-all]');
      this.previousProductsButton = this.querySelector('[data-collection-products-previous]');
      this.nextProductsButton = this.querySelector('[data-collection-products-next]');
      this.cache = new Map();
      this.activeHandle = '';
      this.observer = null;

      if (!this.configElement || !this.panel || !this.grid || !this.tabs.length) return;

      try {
        this.config = JSON.parse(this.configElement.textContent);
      } catch (error) {
        return;
      }

      this.dataset.initialized = 'true';

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab, true));
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, tab));
      });

      this.previousProductsButton?.addEventListener('click', () => this.scrollProducts(-1));
      this.nextProductsButton?.addEventListener('click', () => this.scrollProducts(1));
      this.grid.addEventListener('scroll', () => this.requestArrowUpdate(), { passive: true });
      window.addEventListener('resize', () => this.requestArrowUpdate(), { passive: true });

      const initialTab =
        this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true' && !tab.disabled) ||
        this.tabs.find((tab) => !tab.disabled);
      if (!initialTab) {
        this.renderMessage(this.config.emptyText, 'empty');
        return;
      }
      this.activateTab(initialTab, false);

      if (this.config.autoLoadFirst) {
        this.observeInitialTab(initialTab);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    observeInitialTab(tab) {
      if (!('IntersectionObserver' in window)) {
        this.loadProducts(tab);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.observer = null;
          this.loadProducts(tab);
        },
        { rootMargin: '300px 0px' }
      );

      this.observer.observe(this);
    }

    handleTabKeydown(event, currentTab) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const enabledTabs = this.tabs.filter((tab) => !tab.disabled);
      const currentIndex = enabledTabs.indexOf(currentTab);
      if (currentIndex === -1 || !enabledTabs.length) return;
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledTabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = enabledTabs.length - 1;

      enabledTabs[nextIndex].focus();
      this.activateTab(enabledTabs[nextIndex], true);
    }

    activateTab(tab, shouldLoad) {
      if (!tab || tab.disabled) return;

      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.tabIndex = isActive ? 0 : -1;
        item.classList.toggle('is-active', isActive);
      });

      this.activeHandle = tab.dataset.collectionHandle || '';
      this.panel.setAttribute('aria-labelledby', tab.id);

      if (shouldLoad) {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        this.loadProducts(tab);
      }
    }

    async loadProducts(tab) {
      const handle = tab && tab.dataset.collectionHandle;
      if (!handle) {
        this.renderMessage(this.config.emptyText, 'empty');
        return;
      }

      this.activeHandle = handle;
      this.renderLoading();

      try {
        const responseData = await this.fetchProducts(handle);
        if (this.activeHandle !== handle) return;

        this.renderProducts(responseData, tab.dataset.collectionUrl || '');
      } catch (error) {
        if (this.activeHandle !== handle) return;
        this.renderError(tab);
      }
    }

    fetchProducts(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);

      const rootUrl = this.normalizeRootUrl(this.config.rootUrl);
      const productLimit = Number(this.config.limit) || 6;
      let rendererId = 'collection-tab-products-ajax';
      if (productLimit <= 6) rendererId = 'collection-tab-products-ajax-6';
      else if (productLimit <= 12) rendererId = 'collection-tab-products-ajax-12';

      const requestUrl = `${rootUrl}collections/${encodeURIComponent(handle)}?section_id=${rendererId}`;
      const request = fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const responseRoot = documentFragment.querySelector('[data-collection-tab-products-response]');
          const responseList = responseRoot && responseRoot.querySelector('[data-collection-tab-products-list]');
          if (!responseRoot || !responseList) throw new Error('Collection response markup is invalid');

          return {
            collectionUrl: responseRoot.dataset.collectionUrl || '',
            items: Array.from(responseList.children).map((item) => item.outerHTML)
          };
        })
        .catch((error) => {
          this.cache.delete(handle);
          throw error;
        });

      this.cache.set(handle, request);
      return request;
    }

    renderLoading() {
      this.panel.hidden = false;
      this.panel.setAttribute('aria-busy', 'true');
      this.grid.hidden = false;
      this.grid.replaceChildren();
      this.status.hidden = false;
      delete this.status.dataset.state;
      this.status.textContent = this.config.loadingText;
      if (this.viewAll) this.viewAll.hidden = true;
      this.hideProductArrows();

      const skeletonCount = Math.min(Number(this.config.limit) || 6, Number(this.config.desktopColumns) || 6);
      for (let index = 0; index < skeletonCount; index += 1) {
        const skeleton = document.createElement('li');
        skeleton.className = 'collection-image-tabs__skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        skeleton.innerHTML = '<span class="collection-image-tabs__skeleton-media"></span><span class="collection-image-tabs__skeleton-line"></span><span class="collection-image-tabs__skeleton-line collection-image-tabs__skeleton-line--short"></span>';
        this.grid.appendChild(skeleton);
      }
    }

    renderProducts(responseData, fallbackCollectionUrl) {
      this.panel.hidden = false;
      this.panel.setAttribute('aria-busy', 'false');
      this.grid.replaceChildren();

      const items = responseData && Array.isArray(responseData.items) ? responseData.items : [];
      if (!items.length) {
        this.renderMessage(this.config.emptyText, 'empty');
        return;
      }

      const fragment = document.createDocumentFragment();
      items.slice(0, this.config.limit).forEach((itemHtml) => {
        const template = document.createElement('template');
        template.innerHTML = itemHtml.trim();
        const item = template.content.firstElementChild;
        if (!item) return;

        this.scopeCardIds(item);
        fragment.appendChild(item);
      });
      this.grid.appendChild(fragment);
      this.grid.hidden = false;
      this.grid.scrollLeft = 0;
      this.status.hidden = true;
      delete this.status.dataset.state;
      this.status.textContent = '';

      const collectionUrl = responseData.collectionUrl || fallbackCollectionUrl;
      if (this.viewAll) {
        this.viewAll.href = collectionUrl || '#';
        this.viewAll.hidden = !this.config.showViewAll || !collectionUrl;
      }

      this.requestArrowUpdate();

      this.dispatchEvent(
        new CustomEvent('collection-tabs:rendered', {
          bubbles: true,
          detail: { collectionUrl, itemCount: this.grid.children.length }
        })
      );
    }

    scopeCardIds(item) {
      const suffix = `-${this.id || 'collection-tabs'}`;
      const idMap = new Map();
      item.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.id;
        const newId = `${oldId}${suffix}`;
        idMap.set(oldId, newId);
        element.id = newId;
      });

      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attribute) => {
        item.querySelectorAll(`[${attribute}]`).forEach((element) => {
          const value = element.getAttribute(attribute);
          if (!value) return;
          element.setAttribute(
            attribute,
            value
              .split(/\s+/)
              .map((token) => idMap.get(token) || token)
              .join(' ')
          );
        });
      });
    }

    renderMessage(message, state) {
      this.panel.hidden = false;
      this.panel.setAttribute('aria-busy', 'false');
      this.grid.replaceChildren();
      this.grid.hidden = true;
      this.status.hidden = false;
      this.status.dataset.state = state;
      this.status.textContent = message;
      if (this.viewAll) this.viewAll.hidden = true;
      this.hideProductArrows();
    }

    scrollProducts(direction) {
      if (!this.grid || !window.matchMedia('(min-width: 750px)').matches) return;

      const item = this.grid.querySelector('.collection-image-tabs__product-item, .collection-image-tabs__skeleton');
      const styles = window.getComputedStyle(this.grid);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const distance = item ? item.getBoundingClientRect().width + gap : this.grid.clientWidth / 5;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      this.grid.scrollBy({ left: direction * distance, behavior });
    }

    requestArrowUpdate() {
      window.requestAnimationFrame(() => this.updateProductArrows());
    }

    updateProductArrows() {
      if (!this.grid || !this.previousProductsButton || !this.nextProductsButton) return;

      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const maxScroll = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
      const hasOverflow = isDesktop && !this.grid.hidden && maxScroll > 2;

      this.previousProductsButton.hidden = !hasOverflow || this.grid.scrollLeft <= 2;
      this.nextProductsButton.hidden = !hasOverflow || this.grid.scrollLeft >= maxScroll - 2;
    }

    hideProductArrows() {
      if (this.previousProductsButton) this.previousProductsButton.hidden = true;
      if (this.nextProductsButton) this.nextProductsButton.hidden = true;
    }

    renderError(tab) {
      this.renderMessage(this.config.errorText, 'error');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'collection-image-tabs__retry';
      retry.textContent = this.config.retryText;
      retry.addEventListener('click', () => {
        this.cache.delete(tab.dataset.collectionHandle);
        this.loadProducts(tab);
      });
      this.status.appendChild(retry);
    }

    normalizeRootUrl(rootUrl) {
      const value = rootUrl || '/';
      return value.endsWith('/') ? value : `${value}/`;
    }
  }

  customElements.define('collection-image-tabs', CollectionImageTabs);
})();
